/* eslint-disable */
/* TODO: Fix Eslint issues */

/**
 *
 * /ReactTable
 *
 */

import React, { memo, useEffect, useMemo, useState, useRef } from 'react'
import {
	useTable,
	useSortBy,
	useFilters,
	useGlobalFilter,
	useFlexLayout,
	useResizeColumns,
	usePagination,
	useAsyncDebounce,
	useExpanded,
} from 'react-table'

import { matchSorter } from 'match-sorter'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { format } from 'date-fns'
import { TableStyles } from './styles'
import AnimateHeight from 'react-animate-height'

//*************************************
// column API notes
//*************************************

// show: false,      (only hides column if value === false. The column must have an id.)
// width: 150, 		 for flex-basis and flex-grow
// minWidth: 30,     limit for resizing
// maxWidth: 400,    limit for resizing

// disableSortBy: true     (sorting is allowed by default)
// sortType: 'alphanumeric' (default), 'basic', or 'datetime'

// disableFilterBy: true   (filtering is allowed by default)
// filterType: 'fuzzyText' (default), 'contains', or 'equal'
// Filter: SelectFilter(
// 		<React.Fragment>
// 			<option value="">All</option>
// 			<option value={true}>Yes</option>     <- pass options with values matching the values of the data
// 			<option value={false}>No</option>
// 		</React.Fragment>
// 	),

// Date and time utilities
// import {dateAccessor, dateCell, dateTimeCell} from 'components/ReactTable'

// 	accessor: dateAccessor('created_at'),
//  sortType: 'datetime',
// 	Cell: dateTimeCell,  or  dateCell

//*************************************

// Define date and time utilities
export const dateCell = ({ value }) => <span>{format(value, 'MMM do, yyyy')}</span>

export const dateTimeCell = ({ value }) => <span>{format(value, 'MMM do, yyyy h:mma')}</span>

export const dateAccessor = (property_name) => (object) => new Date(object[property_name])

// Define filter UI's
export const SelectFilter =
	(options) =>
	({ column: { filterValue, setFilter } }) =>
		(
			<select
				onClick={(e) => e.stopPropagation()}
				onChange={(e) => setFilter(e.target.value || undefined)}
				className="filter-input"
				value={filterValue || ''}
			>
				{options}
			</select>
		)

const DefaultColumnFilter = ({ column: { filterValue, setFilter } }) => (
	<input
		value={filterValue || ''}
		onClick={(e) => e.stopPropagation()}
		onChange={(e) => {
			setFilter(e.target.value || undefined) // Set undefined to remove the filter entirely
		}}
		className="filter-input"
	/>
)

// Define filter functions
const fuzzyTextFilterFn = (rows, id, filterValue) => matchSorter(rows, filterValue, { keys: [(row) => row.values[id]] })

const containsFilterFn = (rows, id, filterValue) =>
	matchSorter(rows, filterValue, { keys: [(row) => row.values[id]], threshold: matchSorter.rankings.CONTAINS })

const equalFilterFn = (rows, id, filterValue) =>
	matchSorter(rows, filterValue, { keys: [(row) => row.values[id]], threshold: matchSorter.rankings.EQUAL })

// Let the table remove the filter if the string is empty
fuzzyTextFilterFn.autoRemove = (val) => !val
containsFilterFn.autoRemove = (val) => !val
equalFilterFn.autoRemove = (val) => !val

const filter_types = {
	fuzzyText: fuzzyTextFilterFn,
	contains: containsFilterFn,
	equal: equalFilterFn,
}

// Define default column
const default_column = {
	filter: 'fuzzyText',
	Filter: DefaultColumnFilter,
	minWidth: 30, // limit for resizing
	width: 150, // for flex-basis and flex-grow
	maxWidth: 400, // limit for resizing
}

// Define expander column to use when SubComponent is provided
const expander_column = {
	Header: () => null,
	id: 'expander',
	width: 20,
	minWidth: 20,
	maxWidth: 30,
	Cell: ({ row }) => (
		<div className="small text-center" {...row.getToggleRowExpandedProps()}>
			{row.isExpanded ? '▼' : '►'}
		</div>
	),
	className: 'expander-column',
}

function ReactTable({
	columns,
	data = [],
	defaultPageSize = 10,
	loading,
	noDataText,
	TrComponent,
	SubComponent,
	defaultSortBy,
	hide_headers,
	hide_pagination_component,
	onFetchData, // onFetchData and props below are for tables with server-side pagination.
	total_pages,
	controlledPageIndex,
	setControlledPage,
	auto_reset_page = true,
	compact = false,
}) {
	const [display_page_number, displayPageNumber] = useState(1) // only controls the value of the page number input, not the actual pagination.

	const memoized_columns = useMemo(() => {
		const needs_expander = SubComponent && !columns.some((col) => col.id === 'expander')
		return needs_expander ? [expander_column, ...columns] : columns
	}, [columns, SubComponent])

	const [table_data, setTableData] = useState(data)
	const skipPageResetRef = useRef()

	useEffect(() => {
		skipPageResetRef.current = !auto_reset_page
	}, [auto_reset_page])

	useEffect(() => {
		// skipPageResetRef.current = !auto_reset_page // true
		setTableData(data)
	}, [JSON.stringify(data)])

	useEffect(() => {
		skipPageResetRef.current = !auto_reset_page // false
	})

	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		prepareRow,
		page,
		canNextPage,
		pageOptions,
		gotoPage,
		nextPage,
		previousPage,
		setPageSize,
		state: { pageIndex, pageSize, sortBy, filters },
	} = useTable(
		{
			columns: memoized_columns,
			data: table_data,
			manualSortBy: Boolean(onFetchData),
			manualFilters: Boolean(onFetchData),
			manualPagination: Boolean(onFetchData),
			useControlledState: Boolean(setControlledPage)
				? (state) =>
						useMemo(
							() => ({
								...state,
								pageIndex: controlledPageIndex,
							}),
							[state, controlledPageIndex],
						)
				: undefined,
			defaultColumn: useMemo(() => default_column, []),
			filterTypes: useMemo(() => filter_types, []),
			initialState: {
				pageIndex: 0,
				pageSize: defaultPageSize,
				sortBy: defaultSortBy,
				hiddenColumns: columns.filter((col) => col.show === false).map((col) => col.id),
			},
			autoResetPage: !skipPageResetRef.current,
			autoResetExpanded: !skipPageResetRef.current,
			autoResetGroupBy: !skipPageResetRef.current,
			autoResetSelectedRows: !skipPageResetRef.current,
			autoResetSortBy: !skipPageResetRef.current,
			autoResetFilters: !skipPageResetRef.current,
			autoResetRowState: !skipPageResetRef.current,
		},
		useFilters,
		useGlobalFilter,
		useSortBy,
		useFlexLayout,
		useResizeColumns,
		useExpanded,
		usePagination,
	)

	const onFetchDataDebounced = onFetchData && useAsyncDebounce(onFetchData, 100)

	useEffect(() => {
		onFetchDataDebounced && onFetchDataDebounced({ pageIndex, pageSize, sortBy, filters })
	}, [pageIndex, pageSize, sortBy, filters])

	useEffect(
		() => displayPageNumber(/* controlledPageIndex ? (controlledPageIndex + 1) : */ pageIndex + 1),
		[pageIndex /*, controlledPageIndex*/],
	)

	return (
		<TableStyles style={{ opacity: loading ? '0.5' : '1' }}>
			<div
				{...getTableProps()}
				className={classNames('table', {
					compact: compact,
				})}
			>
				{!hide_headers && (
					<div className="thead">
						{headerGroups.map((headerGroup) => (
							<div className="tr" {...headerGroup.getHeaderGroupProps()}>
								{headerGroup.headers.map((column, index) => {
									const {
										render,
										getHeaderProps,
										isSorted,
										isSortedDesc,
										getSortByToggleProps,
										canFilter,
										getResizerProps,
									} = column

									return (
										<div
											{...getHeaderProps(getSortByToggleProps())}
											className={classNames('th', {
												'header-sort-asc': isSorted && !isSortedDesc,
												'header-sort-desc': isSorted && isSortedDesc,
												'd-flex align-items-center': !canFilter,
											})}
											key={index}
										>
											<div className="resizer" {...getResizerProps()} />
											<div>
												{render('Header')}{' '}
												{isSortedDesc && <FontAwesomeIcon icon="long-arrow-alt-down" />}
												{isSorted && !isSortedDesc && (
													<FontAwesomeIcon icon="long-arrow-alt-up" />
												)}
											</div>
											<div>{canFilter && !column.disableFilterBy && render('Filter')}</div>
										</div>
									)
								})}
							</div>
						))}
					</div>
				)}
				<div className="tbody" {...getTableBodyProps()}>
					{page.map((row, index) => {
						prepareRow(row)
						return (
							<React.Fragment key={index}>
								<TrComponent className="tr" row={row} {...row.getRowProps()}>
									{row.cells.map((cell, cell_index) => (
										<div
											className={'td ' + (cell.column.className || '')}
											{...cell.getCellProps()}
											key={cell_index}
										>
											{cell.render('Cell')}
										</div>
									))}
								</TrComponent>
								{SubComponent && (
									<AnimateHeight height={row.isExpanded ? 'auto' : 0}>
										<SubComponent row={row} />
									</AnimateHeight>
								)}
							</React.Fragment>
						)
					})}
					{data.length === 0 && !loading && (
						<div className="tr">
							<div className="td text-center py-4">{noDataText}</div>
						</div>
					)}
				</div>
			</div>
			{!hide_pagination_component && (
				<div className="d-flex flex-wrap table-controls justify-content-end">
					<span className="pagination-element">
						Page{' '}
						<input
							type="number"
							value={display_page_number}
							className="page-number-input"
							onChange={(e) => displayPageNumber(e.target.value)}
							onBlur={(e) => gotoPage(Number(e.target.value - 1))}
							onKeyDown={(e) => e.key === 'Enter' && gotoPage(Number(e.target.value - 1))}
							disabled={Boolean(setControlledPage)} // couldn't get page-skipping to work with server-side pagination but it still shows the page.
						/>
						{total_pages || pageOptions.length ? ` of ${total_pages || pageOptions.length}` : ''}
					</span>
					<span className="pagination-element">
						Rows per page:&nbsp;
						<select
							value={pageSize}
							onChange={(e) => setPageSize(Number(e.target.value))}
							className="rows-per-page clickable"
						>
							{[5, 10, 20, 25, 50, 100].map((size) => (
								<option key={size}>{size}</option>
							))}
						</select>
					</span>
					<div className="py-1">
						<button
							onClick={() =>
								setControlledPage ? setControlledPage(controlledPageIndex - 1) : previousPage()
							}
							disabled={display_page_number <= 1}
							type="button"
							className="pagination-element btn-unstyled"
						>
							<FontAwesomeIcon icon="chevron-left" transform="grow-3" />
						</button>
						<button
							onClick={() => {
								setControlledPage ? setControlledPage(controlledPageIndex + 1) : nextPage()
							}}
							disabled={setControlledPage ? display_page_number === total_pages : !canNextPage} // should not be disabled if total_pages is not available.
							type="button"
							className="pagination-element btn-unstyled"
						>
							<FontAwesomeIcon icon="chevron-right" transform="grow-3" />
						</button>
					</div>
				</div>
			)}
		</TableStyles>
	)
}

ReactTable.propTypes = {
	hide_pagination_component: PropTypes.bool,
	hide_headers: PropTypes.bool,
}

ReactTable.defaultProps = {
	TrComponent: (props) => <div {...props} />,
	data: [],
	defaultSortBy: [],
}

export default memo(ReactTable)
